import { supabase } from '@/integrations/supabase/client';

export type ChatFolder = {
  id: string;
  user_id: string;
  name: string;
  color: string;
  icon: string;
  position: number;
  created_at: string;
};

export type CreateFolderInput = {
  name: string;
  color?: string;
  icon?: string;
};

export type UpdateFolderInput = {
  name?: string;
  color?: string;
  icon?: string;
  position?: number;
};

// Default folders for new users
export const DEFAULT_FOLDERS: Omit<ChatFolder, 'id' | 'user_id' | 'created_at'>[] = [
  { name: 'Urbanismo', color: '#3B82F6', icon: '🏗️', position: 0 },
  { name: 'Personal', color: '#10B981', icon: '👥', position: 1 },
  { name: 'Contratación', color: '#F59E0B', icon: '📋', position: 2 },
  { name: 'Hacienda', color: '#8B5CF6', icon: '💰', position: 3 },
];

export async function createFolder(userId: string, input: CreateFolderInput): Promise<ChatFolder> {
  // Get the highest position
  const { data: existingFolders } = await supabase
    .from('chat_folders')
    .select('position')
    .eq('user_id', userId)
    .order('position', { ascending: false })
    .limit(1);

  const nextPosition = existingFolders && existingFolders.length > 0 
    ? (existingFolders[0].position || 0) + 1 
    : 0;

  const { data, error } = await supabase
    .from('chat_folders')
    .insert({
      user_id: userId,
      name: input.name,
      color: input.color || '#6B7280',
      icon: input.icon || '📁',
      position: nextPosition,
    })
    .select()
    .single();

  if (error) throw error;
  return data as ChatFolder;
}

export async function listFolders(userId: string): Promise<ChatFolder[]> {
  const { data, error } = await supabase
    .from('chat_folders')
    .select('*')
    .eq('user_id', userId)
    .order('position', { ascending: true });

  if (error) throw error;
  return data as ChatFolder[];
}

export async function updateFolder(folderId: string, updates: UpdateFolderInput): Promise<void> {
  const { error } = await supabase
    .from('chat_folders')
    .update(updates)
    .eq('id', folderId);

  if (error) throw error;
}

export async function deleteFolder(folderId: string): Promise<void> {
  // First, unassign all chats from this folder
  await supabase
    .from('chats')
    .update({ folder_id: null })
    .eq('folder_id', folderId);

  // Then delete the folder
  const { error } = await supabase
    .from('chat_folders')
    .delete()
    .eq('id', folderId);

  if (error) throw error;
}

export async function reorderFolders(userId: string, folderIds: string[]): Promise<void> {
  // Update each folder's position
  const updates = folderIds.map((id, index) => 
    supabase
      .from('chat_folders')
      .update({ position: index })
      .eq('id', id)
      .eq('user_id', userId)
  );

  await Promise.all(updates);
}

export async function assignChatToFolder(chatId: string, folderId: string | null): Promise<void> {
  const { error } = await supabase
    .from('chats')
    .update({ folder_id: folderId })
    .eq('id', chatId);

  if (error) throw error;
}

export async function initializeDefaultFolders(userId: string): Promise<ChatFolder[]> {
  // Check if user already has folders
  const existingFolders = await listFolders(userId);
  if (existingFolders.length > 0) {
    return existingFolders;
  }

  // Create default folders
  const { data, error } = await supabase
    .from('chat_folders')
    .insert(
      DEFAULT_FOLDERS.map(folder => ({
        ...folder,
        user_id: userId,
      }))
    )
    .select();

  if (error) throw error;
  return data as ChatFolder[];
}
